<div class="min-h-screen flex" [class.login-animating]="isAnimating()">
	<!-- Left Side - 3/4 Width with Primary Color, Logo, Quote and Shapes -->
	<div class="hidden lg:flex bg-primary relative overflow-hidden left-panel" [class.lg:w-3/4]="!isAnimating()" [class.animate-shrink]="isAnimating()">
		<!-- Decorative Background Shapes -->
		<div class="absolute inset-0 overflow-hidden background-decorations" [class.fade-out-content]="isAnimating()">
			<!-- Large Circle Top Right -->
			<div
				class="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-accent/10 animate-float"
			></div>

			<!-- Medium Circle Bottom Left -->
			<div
				class="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-secondary/15 animate-float-delayed"
			></div>

			<!-- Small Circle Middle -->
			<div
				class="absolute top-1/3 right-1/4 w-48 h-48 rounded-full bg-accent/20 animate-pulse-slow"
			></div>

			<!-- Diagonal Lines -->
			<div
				class="absolute top-0 right-0 w-1 h-64 bg-gradient-to-b from-secondary/30 to-transparent rotate-45 transform origin-top-right"
			></div>
			<div
				class="absolute bottom-0 left-1/4 w-1 h-48 bg-gradient-to-t from-secondary/20 to-transparent rotate-12"
			></div>

			<!-- Geometric Pattern -->
			<div class="absolute top-1/2 left-1/3 transform -translate-x-1/2 -translate-y-1/2">
				<div class="w-24 h-24 border-2 border-secondary/20 rotate-45 animate-spin-slow"></div>
			</div>
		</div>

		<!-- Content -->
		<div class="relative z-10 flex flex-col items-center justify-center w-full px-16 text-center content-wrapper" [class.fade-out-content]="isAnimating()">
			<!-- Logo -->
			<div class="mb-12 animate-fade-in-up logo-container" [class.animate-logo-to-sidebar]="isAnimating()">
				<app-logo [variant]="'light'" [size]="'xl'" [isLink]="false"></app-logo>
			</div>

			<!-- Quote -->
			<div class="max-w-2xl animate-fade-in-up animation-delay-200">
				<blockquote class="mb-8">
					<p class="text-3xl font-light text-mist/90 leading-relaxed mb-6 italic">
						"Where Elegance Meets Excellence"
					</p>
					<p class="text-lg text-secondary/90 font-light leading-relaxed">
						Experience the artistry of hair transformation in a space designed for luxury and comfort
					</p>
				</blockquote>
			</div>

			<!-- Decorative Element -->
			<div class="mt-12 flex items-center gap-4 animate-fade-in-up animation-delay-400">
				<div class="w-16 h-px bg-secondary/50"></div>
				<svg class="w-8 h-8 text-secondary/70" fill="currentColor" viewBox="0 0 24 24">
					<path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
				</svg>
				<div class="w-16 h-px bg-secondary/50"></div>
			</div>

			<!-- Bottom Text -->
			<div class="absolute bottom-12 left-0 right-0 text-center animate-fade-in-up animation-delay-600">
				<p class="text-mist/60 text-sm font-light">
					Hyderabad's Premier Hair Studio
				</p>
			</div>
		</div>
	</div>

	<!-- Right Side - 1/4 Width with Login Form -->
	<div class="w-full lg:w-1/4 bg-mist flex items-center justify-center p-8 lg:p-12 right-panel" [class.fade-out-content]="isAnimating()">
		<div class="w-full max-w-md">
			<!-- Back Button -->
			<button
				type="button"
				(click)="goBack()"
				class="group mb-6 flex items-center gap-2 text-charcoal/60 hover:text-primary transition-all duration-300 animate-fade-in"
			>
				<svg class="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
				</svg>
				<span class="text-sm font-medium">Back</span>
			</button>

			<!-- Mobile Logo (shown only on mobile) -->
			<div class="lg:hidden text-center mb-8">
				<app-logo [variant]="'dark'" [size]="'sm'" [isLink]="false"></app-logo>
			</div>

			<!-- Welcome Text -->
			<div class="mb-8 animate-fade-in">
				<h2 class="text-3xl font-bold text-charcoal mb-2">Welcome Back</h2>
				<p class="text-charcoal/60 text-sm">Sign in to your account</p>
			</div>

			<!-- Error Message -->
			@if (showError()) {
			<div
				class="mb-6 p-4 bg-red-50 border-l-4 border-primary rounded-lg animate-shake"
			>
				<div class="flex items-start">
					<svg
						class="w-5 h-5 text-primary mt-0.5 mr-3 flex-shrink-0"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
						></path>
					</svg>
					<div class="flex-1">
						<p class="text-sm text-charcoal font-medium">{{ errorMessage() }}</p>
					</div>
					<button
						type="button"
						(click)="showError.set(false)"
						class="ml-2 text-charcoal/60 hover:text-charcoal"
					>
						<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M6 18L18 6M6 6l12 12"
							></path>
						</svg>
					</button>
				</div>
			</div>
			}

			<!-- Login Form -->
			<form [formGroup]="loginForm" (ngSubmit)="onSubmit()" class="space-y-5 animate-fade-in animation-delay-200">
				<!-- Email Field -->
				<div>
					<label for="email" class="block text-sm font-semibold text-charcoal mb-2">
						Email Address
					</label>
					<input
						type="email"
						id="email"
						formControlName="email"
						[class.border-red-500]="isFieldInvalid('email')"
						class="w-full px-4 py-3 bg-white border-2 border-charcoal/20 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none text-charcoal placeholder-charcoal/40"
						placeholder="you@example.com"
					/>
					@if (isFieldInvalid('email')) {
					<p class="mt-1.5 text-xs text-red-500">{{ getFieldError('email') }}</p>
					}
				</div>

				<!-- Password Field -->
				<div>
					<label for="password" class="block text-sm font-semibold text-charcoal mb-2">
						Password
					</label>
					<div class="relative">
						<input
							[type]="showPassword() ? 'text' : 'password'"
							id="password"
							formControlName="password"
							[class.border-red-500]="isFieldInvalid('password')"
							class="w-full px-4 py-3 bg-white border-2 border-charcoal/20 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none text-charcoal placeholder-charcoal/40 pr-12"
							placeholder="Enter your password"
						/>
						<button
							type="button"
							(click)="togglePasswordVisibility()"
							class="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/50 hover:text-charcoal transition-colors"
						>
							@if (showPassword()) {
							<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
								></path>
							</svg>
							} @else {
							<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
								></path>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
								></path>
							</svg>
							}
						</button>
					</div>
					@if (isFieldInvalid('password')) {
					<p class="mt-1.5 text-xs text-red-500">{{ getFieldError('password') }}</p>
					}
				</div>

				<!-- Remember Me & Forgot Password -->
				<div class="flex items-center justify-between text-sm">
					<label class="flex items-center cursor-pointer group">
						<input
							type="checkbox"
							formControlName="rememberMe"
							class="w-4 h-4 rounded border-2 border-charcoal/30 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer"
						/>
						<span class="ml-2 text-charcoal/70 group-hover:text-charcoal transition-colors">Remember me</span>
					</label>
					<a routerLink="/forgot-password" class="text-primary hover:text-accent font-medium transition-colors">
						Forgot password?
					</a>
				</div>

				<!-- Sign In Button -->
				<button
					type="submit"
					[disabled]="isSubmitting()"
					class="w-full bg-primary hover:bg-primary-75 text-mist font-semibold py-3.5 rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
				>
					@if (isSubmitting()) {
					<svg
						class="animate-spin h-5 w-5"
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
					>
						<circle
							class="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							stroke-width="4"
						></circle>
						<path
							class="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
						></path>
					</svg>
					<span>Signing in...</span>
					} @else {
					<span>Sign In</span>
					<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M14 5l7 7m0 0l-7 7m7-7H3"
						></path>
					</svg>
					}
				</button>
			</form>

			<!-- Divider -->
			<!-- <div class="relative my-8 animate-fade-in animation-delay-400">
				<div class="absolute inset-0 flex items-center">
					<div class="w-full border-t border-charcoal/20"></div>
				</div>
				<div class="relative flex justify-center text-sm">
					<span class="px-4 bg-mist text-charcoal/60">Or continue with</span>
				</div>
			</div> -->

			<!-- Google Sign In -->
			<!-- <button
				type="button"
				(click)="signInWithGoogle()"
				class="w-full bg-white border-2 border-charcoal/20 hover:border-charcoal/40 text-charcoal font-semibold py-3.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-center gap-3 animate-fade-in animation-delay-600"
			>
				<svg class="w-5 h-5" viewBox="0 0 24 24">
					<path
						fill="#4285F4"
						d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
					/>
					<path
						fill="#34A853"
						d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
					/>
					<path
						fill="#FBBC05"
						d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
					/>
					<path
						fill="#EA4335"
						d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
					/>
				</svg>
				<span>Sign in with Google</span>
			</button> -->

			<!-- Sign Up Link -->
			<!-- <p class="text-center text-sm text-charcoal/60 mt-8 animate-fade-in animation-delay-800">
				Don't have an account?
				<a routerLink="/signup" class="text-primary hover:text-accent font-semibold ml-1 transition-colors">
					Sign up
				</a>
			</p> -->
		</div>
	</div>
</div>
